import React, { useState } from 'react';
import { publishSharedOffer } from '@/lib/sharedOffers';
import { Card, CardContent, CardHeader } from '@/ui/components/ui/card';
import { Button } from '@/ui/components/ui/button';
import { useAppContext, type Project } from '@/store/AppContext';
import {
  FileText, Send, CheckCircle2, XCircle, Search, Filter,
  ArrowUpDown, Eye, Calendar, MapPin, Trash2, AlertTriangle,
} from 'lucide-react';
import { Input } from '@/ui/components/ui/input';
import {
  getProjectListRow, isProjectListStatus, PROJECT_LIST_STATUS_LABELS,
  type ProjectListRow,
} from '@/lib/projectListRow';

interface ProjectsListProps {
  onEditProject?: (project: Project) => void;
}

type SortKey = 'name' | 'location' | 'status' | 'totalDiscountedCost' | 'createdAt';

const STATUS_APPEARANCE = {
  draft: { icon: FileText, color: 'bg-slate-100 text-slate-600' },
  sent: { icon: Send, color: 'bg-amber-100 text-amber-600' },
  accepted: { icon: CheckCircle2, color: 'bg-blue-100 text-blue-600' },
  in_progress: { icon: Calendar, color: 'bg-emerald-100 text-emerald-600' },
  completed: { icon: CheckCircle2, color: 'bg-slate-900 text-white' },
  rejected: { icon: XCircle, color: 'bg-red-100 text-red-600' },
  cancelled: { icon: XCircle, color: 'bg-slate-100 text-slate-600' },
} satisfies Record<Project['status'], { icon: typeof FileText; color: string }>;

function StatusBadge({ row }: { row: ProjectListRow }): React.ReactElement | null {
  const appearance = STATUS_APPEARANCE[row.status];
  if (!appearance) return null;
  const { icon: Icon, color } = appearance;
  return (
    <div className="flex flex-wrap items-center gap-2">
      <span className={`px-2 py-1 ${color} rounded-full text-xs lg:text-[10px] font-bold uppercase tracking-wider inline-flex items-center gap-1`}>
        <Icon className="w-3 h-3 shrink-0" /> {row.statusLabel}
      </span>
      {row.isNewAcceptance && (
        <span className="px-2 py-1 bg-amber-500 text-white rounded-full text-[10px] font-black uppercase tracking-widest animate-pulse">
          NOWA!
        </span>
      )}
    </div>
  );
}

function ProjectPrice({ row }: { row: ProjectListRow }): React.ReactElement {
  return (
    <div className="min-w-0 space-y-0.5">
      <p className="font-black text-white [overflow-wrap:anywhere] lg:[overflow-wrap:normal]">{row.price}</p>
      <div className="flex flex-wrap lg:flex-nowrap items-center gap-1">
        <span className={`text-[8px] font-black px-1 rounded uppercase ${
          row.priceMode === 'premium' ? 'bg-emerald-100 text-emerald-700' :
          row.priceMode === 'cheap' ? 'bg-red-100 text-red-700' : 'bg-blue-100 text-blue-700'
        }`}>
          {row.priceMode}
        </span>
        <p className="text-[10px] text-emerald-300 font-black [overflow-wrap:anywhere] lg:[overflow-wrap:normal]">Zysk: +{row.profit}</p>
      </div>
      {row.waitingDays !== null && (
        <p className="text-[10px] text-amber-300 font-medium italic">
          Czeka: {row.waitingDays} dni
        </p>
      )}
    </div>
  );
}

function StatusOptions(): React.ReactElement {
  return <>{Object.entries(PROJECT_LIST_STATUS_LABELS).map(([value, label]) => (
    <option key={value} value={value}>{label}</option>
  ))}</>;
}

export function ProjectsList({ onEditProject }: ProjectsListProps) {
  const { projects, updateProjectStatus, deleteProject } = useAppContext();
  const [searchTerm, setSearchTerm] = useState('');
  const [sharingId, setSharingId] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<Project['status'] | 'all'>('all');
  const [sortConfig, setSortConfig] = useState<{ key: SortKey; direction: 'asc' | 'desc' } | null>(null);

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [projectToDelete, setProjectToDelete] = useState<Project | null>(null);

  const handleDeleteClick = (e: React.MouseEvent, project: Project): void => {
    e.stopPropagation();
    setProjectToDelete(project);
    setShowDeleteModal(true);
  };

  const confirmDelete = async (): Promise<void> => {
    if (projectToDelete) {
      await deleteProject(projectToDelete.id);
    }
    setShowDeleteModal(false);
    setProjectToDelete(null);
  };

  const cancelDelete = (): void => {
    setShowDeleteModal(false);
    setProjectToDelete(null);
  };

  const handleSort = (key: SortKey): void => {
    let direction: 'asc' | 'desc' = 'asc';
    if (sortConfig?.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const filteredProjects = projects
    .filter(p => {
      const matchesSearch = p.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                            p.location?.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesStatus = statusFilter === 'all' || p.status === statusFilter;
      return matchesSearch && matchesStatus;
    })
    .sort((a, b) => {
      if (!sortConfig) return 0;
      const { key, direction } = sortConfig;
      const valA = a[key];
      const valB = b[key];
      if (valA === undefined || valB === undefined) return 0;
      if (valA < valB) return direction === 'asc' ? -1 : 1;
      if (valA > valB) return direction === 'asc' ? 1 : -1;
      return 0;
    });
  const rows = filteredProjects.map(project => ({ project, row: getProjectListRow(project) }));

  const renderActions = (project: Project): React.ReactElement => (
    <div className="grid grid-cols-2 gap-2 lg:flex lg:items-center lg:justify-end">
      <Button
        variant="ghost"
        size="sm"
        className="min-h-14 h-auto px-2 text-emerald-300 hover:bg-emerald-500/10 hover:text-emerald-200 lg:min-h-0 lg:h-8"
        disabled={sharingId !== null}
        onClick={() => { void (async () => {
          setSharingId(project.id);
          try {
            const shareUrl = await publishSharedOffer(project.id);
            await navigator.clipboard.writeText(shareUrl);
            alert('Link do oferty został skopiowany! Link wygasa po 7 dniach lub zmianie projektu.');
          } catch (error) {
            alert(error instanceof Error ? error.message : 'Nie udało się udostępnić oferty.');
          } finally { setSharingId(null); }
        })(); }}
      >
        <Send className="w-4 h-4 mr-1" /> {sharingId === project.id ? 'Udostępnianie…' : 'Wyślij'}
      </Button>
      <Button
        variant="ghost"
        size="sm"
        className="min-h-14 h-auto px-2 text-blue-300 hover:bg-blue-500/10 hover:text-blue-200 lg:min-h-0 lg:h-8"
        onClick={() => onEditProject?.(project)}
      >
        <Eye className="w-4 h-4 mr-1" /> Edytuj
      </Button>
      <Button
        variant="ghost"
        size="sm"
        className="min-h-14 h-auto px-2 text-red-300 hover:bg-red-500/10 hover:text-red-200 lg:min-h-0 lg:h-8"
        onClick={(e) => { handleDeleteClick(e, project); }}
      >
        <Trash2 className="w-4 h-4 mr-1" /> Usuń
      </Button>
      <select
        aria-label={`Status projektu: ${project.name}`}
        className="min-w-0 w-full min-h-14 text-sm border rounded p-2 bg-[#0d0d0d] border-[#2a2a2a] text-white focus:outline-none lg:w-auto lg:min-h-0 lg:text-xs lg:p-1"
        value={project.status}
        onChange={(e) => {
          if (isProjectListStatus(e.target.value)) {
            void updateProjectStatus(project.id, e.target.value);
          }
        }}
      >
        <StatusOptions />
      </select>
    </div>
  );

  const emptyState = (
    <div className="space-y-1">
      <p className="font-bold text-white">{projects.length === 0 ? 'Brak projektów' : 'Nie znaleziono projektów'}</p>
      <p className="text-sm">
        {projects.length === 0 ? 'Utwórz pierwszą wycenę.' : 'Zmień filtr lub wyszukiwaną frazę.'}
      </p>
    </div>
  );

  return (
    <div className="min-w-0 p-4 space-y-6 lg:p-8 lg:space-y-8 bg-[#0f0f0f] min-h-screen text-white">
      <div>
        <h2 className="text-3xl font-bold tracking-tight text-white">Moje Projekty</h2>
        <p className="text-slate-300 mt-2">Zarządzaj wszystkimi swoimi wycenami i realizacjami.</p>
      </div>

      <Card className="min-w-0 bg-[#1a1a1a] border-[#2a2a2a]">
        <CardHeader className="p-4 lg:p-6 lg:pb-4">
          <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
            <div className="relative min-w-0 w-full lg:flex-1 lg:max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                aria-label="Szukaj projektów"
                placeholder="Szukaj po nazwie lub lokalizacji..."
                className="min-h-14 lg:min-h-0 pl-10 bg-[#0d0d0d] border-[#2a2a2a] text-white"
                value={searchTerm}
                onChange={(e) => { setSearchTerm(e.target.value); }}
              />
            </div>
            <div className="flex min-w-0 w-full items-center gap-3 lg:w-auto">
              <div className="flex min-w-0 w-full lg:w-auto items-center gap-2 bg-[#0d0d0d] border border-[#2a2a2a] rounded-lg px-3 py-1">
                <Filter className="w-4 h-4 shrink-0 text-slate-400" />
                <select
                  aria-label="Filtr statusu"
                  className="min-w-0 w-full min-h-14 lg:w-auto lg:min-h-0 bg-transparent text-sm font-medium focus:outline-none text-white"
                  value={statusFilter}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (value === 'all' || isProjectListStatus(value)) setStatusFilter(value);
                  }}
                >
                  <option value="all">Wszystkie statusy</option>
                  <StatusOptions />
                </select>
              </div>
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <div className="space-y-3 p-4 pt-0 lg:hidden">
            {rows.map(({ project, row }) => (
              <article key={project.id} aria-label={row.name} className="min-w-0 rounded-lg border border-[#2a2a2a] p-4 space-y-4">
                <h3 className="font-bold text-white [overflow-wrap:anywhere]">{row.name}</h3>
                <StatusBadge row={row} />
                <div className="space-y-1 text-sm text-slate-300 [overflow-wrap:anywhere]">
                  <p>Klient: {row.client}</p>
                  <p>{row.area}</p>
                  <p className="flex items-start gap-1"><MapPin className="w-4 h-4 shrink-0 mt-0.5" /><span className="min-w-0">{row.location}</span></p>
                  <p className="flex items-start gap-1"><Calendar className="w-4 h-4 shrink-0 mt-0.5" /><span className="min-w-0">{row.date}</span></p>
                  <p>Data utworzenia: {row.createdDate}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-300">Cena brutto dla klienta</p>
                  <ProjectPrice row={row} />
                </div>
                {renderActions(project)}
              </article>
            ))}
            {rows.length === 0 && (
              <div className="py-8 text-center text-slate-300">{emptyState}</div>
            )}
          </div>
          <div className="hidden lg:block overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-[#0d0d0d] border-y border-[#2a2a2a]">
                  <th className="px-6 py-4 text-left font-semibold text-slate-300 cursor-pointer hover:text-white" onClick={() => { handleSort('name'); }}>
                    <div className="flex items-center gap-2">Projekt <ArrowUpDown className="w-3 h-3" /></div>
                  </th>
                  <th className="px-6 py-4 text-left font-semibold text-slate-300 cursor-pointer hover:text-white" onClick={() => { handleSort('location'); }}>
                    <div className="flex items-center gap-2">Lokalizacja <ArrowUpDown className="w-3 h-3" /></div>
                  </th>
                  <th className="px-6 py-4 text-left font-semibold text-slate-300 cursor-pointer hover:text-white" onClick={() => { handleSort('status'); }}>
                    <div className="flex items-center gap-2">Status <ArrowUpDown className="w-3 h-3" /></div>
                  </th>
                  <th className="px-6 py-4 text-left font-semibold text-slate-300 cursor-pointer hover:text-white" onClick={() => { handleSort('totalDiscountedCost'); }}>
                    <div className="flex items-center gap-2">Cena brutto dla klienta <ArrowUpDown className="w-3 h-3" /></div>
                  </th>
                  <th className="px-6 py-4 text-left font-semibold text-slate-300 cursor-pointer hover:text-white" onClick={() => { handleSort('createdAt'); }}>
                    <div className="flex items-center gap-2">Data utworzenia <ArrowUpDown className="w-3 h-3" /></div>
                  </th>
                  <th className="px-6 py-4 text-right font-semibold text-slate-300">Akcje</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#2a2a2a]">
                {rows.map(({ project, row }) => (
                  <tr key={project.id} className="transition-colors hover:bg-[#242424]">
                    <td className="px-6 py-4">
                      <div className="space-y-1">
                        <p className="font-bold text-white">{row.name}</p>
                        <div className="flex flex-wrap items-center gap-3 text-xs text-slate-300">
                          <span>{row.client}</span>
                          <span>{row.area}</span>
                          <span className="flex items-center gap-1"><Calendar className="w-3 h-3" /> {row.date}</span>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className="flex items-center gap-1 text-slate-300"><MapPin className="w-3 h-3" /> {row.location}</span>
                    </td>
                    <td className="px-6 py-4"><StatusBadge row={row} /></td>
                    <td className="px-6 py-4"><ProjectPrice row={row} /></td>
                    <td className="px-6 py-4 text-slate-300">{row.createdDate}</td>
                    <td className="px-6 py-4 text-right">{renderActions(project)}</td>
                  </tr>
                ))}
                {rows.length === 0 && (
                  <tr><td colSpan={6} className="px-6 py-12 text-center text-slate-300">{emptyState}</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {showDeleteModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm">
          <div role="dialog" aria-modal="true" aria-labelledby="delete-project-title" className="min-w-0 max-h-[calc(100dvh-2rem)] overflow-y-auto bg-[#1a1a1a] rounded-xl p-4 lg:p-6 max-w-md w-full mx-4 shadow-2xl border border-[#2a2a2a]">
            <div className="flex items-center gap-3 mb-4">
              <div className="shrink-0 p-3 rounded-full bg-red-100">
                <AlertTriangle className="w-6 h-6 text-red-600" />
              </div>
              <h2 id="delete-project-title" className="min-w-0 text-lg lg:text-xl font-bold text-red-600 uppercase tracking-wider [overflow-wrap:anywhere]">
                UWAGA: USUNIĘCIE PROJEKTU
              </h2>
            </div>
            <p className="text-slate-200 mb-2">Czy chcesz bezpowrotnie usunąć ten projekt?</p>
            <p className="text-white font-bold mb-6 [overflow-wrap:anywhere]">{projectToDelete?.name}</p>
            <p className="text-slate-300 text-sm mb-6">
              Ta akcja jest <span className="text-red-600 font-bold">nieodwracalna</span> i skasuje wszystkie zapisane pozycje wyceny.
            </p>
            <div className="flex flex-col lg:flex-row gap-3">
              <button onClick={cancelDelete} className="min-h-14 lg:min-h-0 flex-1 py-3 px-4 rounded-lg bg-slate-200 text-slate-700 font-semibold hover:bg-slate-300 transition-colors">
                ANULUJ
              </button>
              <button onClick={() => { void confirmDelete(); }} className="min-h-14 lg:min-h-0 flex-1 py-3 px-4 rounded-lg bg-red-600 text-white font-bold uppercase tracking-wider hover:bg-red-700 transition-colors">
                TAK, USUŃ BEZPOWROTNIE
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
